/**
 * useDisplayChannel — connects the screenshare UI to the agent's display channel
 * over a pre-signed WebSocket and collects display messages.
 *
 * Display messages arrive as { type: 'display', data: <payload> } JSON frames.
 * The payload is intentionally open-ended (board, issue card, transition, etc.)
 * so any skill can push its own visual content.
 *
 * Resilience: the screenshare connection is receive-only (it sends one `hello`
 * and then nothing), so without client→server traffic the socket is idle-reaped
 * after ~60-90s and later display frames broadcast to 0 sinks — the screen
 * freezes until a manual refresh. To keep the sink alive we do two things:
 *   1. Keepalive ping every 20s while OPEN (the agent's screenshare branch
 *      installs a receive-only handler, so pings are safely ignored).
 *   2. Auto-reconnect with exponential backoff if the socket closes
 *      unintentionally. On reconnect we re-send the `hello`, and the agent hub
 *      replays its lastFrame — so the current card reappears without a refresh.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type DisplayStatus = 'idle' | 'connecting' | 'live' | 'error' | 'ended';

export interface DisplayMessage {
  /** Kind of content, e.g. 'board' | 'issue' | 'markdown' | custom. */
  kind: string;
  payload: unknown;
  receivedAt: number;
}

/** A single line of conversation transcript, fanned in over the display socket. */
export interface TranscriptLine {
  role: 'participant' | 'bot';
  text: string;
  /** Ms since the session conversation clock started (for an elapsed stamp).
   *  Optional: older frames / malformed values yield undefined. */
  elapsedMs?: number;
}

/**
 * Result of interpreting a single raw WebSocket frame.
 *
 * - `{ kind: 'display', display }` — a display frame that should replace
 *   `current` and append to `history`.
 * - `{ kind: 'transcript', line }` — a transcript line to append.
 * - `null` — the frame is irrelevant or malformed and must be ignored.
 *
 * This is extracted as a pure function so the message-handling behavior
 * (accumulation order, role mapping, malformed-frame tolerance) can be
 * unit-tested without a React renderer, WebSocket, or presigner mock — the
 * screenshare test setup runs in a plain `node` environment.
 */
export type FrameResult =
  | { kind: 'display'; display: DisplayMessage }
  | { kind: 'transcript'; line: TranscriptLine }
  | { kind: 'meta'; meta: { title?: string } }
  | { kind: 'colocation'; voicePresent: boolean }
  | null;

/**
 * Interpret one raw frame (the string payload of a WebSocket `MessageEvent`).
 * Returns a normalized result, or `null` for anything that should be ignored:
 * non-JSON payloads, unknown/missing `type`, or transcript frames with no text.
 * Never throws.
 *
 * @param raw the frame body; typically `evt.data` (a string)
 * @param now timestamp used for `receivedAt` (injectable for tests)
 */
export function interpretFrame(raw: unknown, now: number = Date.now()): FrameResult {
  if (typeof raw !== 'string') return null;
  let msg: { type?: unknown; data?: unknown };
  try {
    msg = JSON.parse(raw) as { type?: unknown; data?: unknown };
  } catch {
    return null;
  }
  if (msg === null || typeof msg !== 'object') return null;

  if (msg.type === 'display') {
    const data = (msg.data ?? {}) as { kind?: string };
    return {
      kind: 'display',
      display: {
        kind: data.kind ?? 'unknown',
        payload: msg.data,
        receivedAt: now,
      },
    };
  }

  if (msg.type === 'transcript') {
    const data = (msg.data ?? {}) as { role?: string; text?: string; elapsedMs?: unknown };
    const text = String(data.text ?? '');
    // Ignore transcript frames with no text.
    if (text.length === 0) return null;
    const elapsedMs =
      typeof data.elapsedMs === 'number' && Number.isFinite(data.elapsedMs) && data.elapsedMs >= 0
        ? data.elapsedMs
        : undefined;
    return {
      kind: 'transcript',
      line: { role: data.role === 'bot' ? 'bot' : 'participant', text, elapsedMs },
    };
  }

  if (msg.type === 'colocation') {
    // Co-location signal from the agent: whether a voice owner is present on the
    // SAME microVM as this screenshare socket. `voicePresent:false` means we
    // landed on the wrong instance (the split) and must reconnect to reshuffle;
    // `true` means display frames will reach us, so we can stop reshuffling.
    const data = (msg.data ?? {}) as { voicePresent?: unknown };
    return { kind: 'colocation', voicePresent: data.voicePresent === true };
  }

  if (msg.type === 'meta') {
    // Session metadata (e.g. a configurable screenshare title). A meta frame
    // NEVER touches current/history/transcript. Only a non-empty string title
    // is carried through; anything else yields an undefined title (ignored by
    // the hook).
    const data = (msg.data ?? {}) as { title?: unknown };
    const title = typeof data.title === 'string' ? data.title : undefined;
    return { kind: 'meta', meta: { title } };
  }

  return null;
}

const PRESIGNER_URL = import.meta.env.VITE_PRESIGNER_URL ?? '/api/presign';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;
const BACKOFF_MULTIPLIER = 2;
const PING_INTERVAL_MS = 20_000;
/**
 * How long to wait for the agent's `colocation` signal confirming a voice owner
 * is on the SAME microVM before giving up on this connection and reconnecting
 * to reshuffle onto a different instance. AgentCore co-location by session id is
 * best-effort: voice and screenshare can land on different microVMs, in which
 * case display frames never reach this socket. If we don't get
 * `voicePresent:true` within this window we close and reconnect (fresh presigned
 * URL) to try landing on the voice instance. Reconnecting from a closed socket
 * reshuffles the AgentCore instance assignment; holding the socket open does
 * not. The window is longer than a typical connect+hello round-trip so a
 * genuinely co-located session is not needlessly churned.
 */
const COLOCATION_WAIT_MS = 4000;
/**
 * One-time delay before the screenshare opens its FIRST WebSocket. Both the
 * voice and screenshare pages auto-open together, so without a stagger their
 * two connections reach AgentCore within ~150-250ms of each other — before
 * either session is established — and AgentCore load-balances them onto
 * DIFFERENT microVMs (the split that makes the screenshare render nothing).
 * Delaying the screenshare lets the voice connection establish and CLAIM a
 * microVM first; AgentCore then routes this later same-session connection to
 * that already-warm instance, so they co-locate on the first try. This mirrors
 * the reference app, where a human opens the display seconds after voice. The
 * delay only applies to the initial connect — reconnects (convergence
 * reshuffles) do NOT re-stagger. If no voice ever opens, this is just a
 * one-time delay before the screenshare connects and sits idle (correct: there
 * is nothing to display).
 */
const INITIAL_STAGGER_MS = 2000;

async function getPresignedWsUrl(sessionId: string): Promise<string> {
  const res = await fetch(PRESIGNER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, channel: 'screenshare' }),
  });
  if (!res.ok) throw new Error(`presigner ${res.status}`);
  const json = (await res.json()) as { wsUrl: string };
  return json.wsUrl;
}

export function useDisplayChannel() {
  const [status, setStatus] = useState<DisplayStatus>('idle');
  const [current, setCurrent] = useState<DisplayMessage | null>(null);
  const [history, setHistory] = useState<DisplayMessage[]>([]);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [title, setTitle] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(INITIAL_BACKOFF_MS);
  const intentionalCloseRef = useRef(false);
  // Remember the session id so a reconnect uses the same one.
  const sessionIdRef = useRef<string | null>(null);
  // Convergence timer: set on open, cleared when the agent confirms co-location
  // (voicePresent:true). If it fires first, this socket landed on the wrong
  // microVM (no voice owner) so we close+reconnect to reshuffle.
  const colocationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True once the agent has confirmed a voice owner is co-located on this
  // socket's microVM. While false we are still trying to converge; while true
  // we hold the socket open with a light keepalive and stop reshuffling.
  const colocatedRef = useRef(false);
  // Re-entrancy guard: true while a connectInternal pass is mid-flight (between
  // tearing down the old socket and the new socket settling open). Prevents a
  // convergence close + a scheduled reconnect from BOTH entering connectInternal
  // and opening overlapping sockets — the connection "storm" where several
  // display sinks register at once on the agent.
  const connectingRef = useRef(false);
  // Timer for the one-time initial stagger, so unmount/disconnect can cancel it.
  const staggerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPing = useCallback(() => {
    if (pingRef.current !== null) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  }, []);

  const clearColocationTimer = useCallback(() => {
    if (colocationTimerRef.current !== null) {
      clearTimeout(colocationTimerRef.current);
      colocationTimerRef.current = null;
    }
  }, []);

  const clearStaggerTimer = useCallback(() => {
    if (staggerTimerRef.current !== null) {
      clearTimeout(staggerTimerRef.current);
      staggerTimerRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // Schedule an auto-reconnect using exponential backoff, unless the close was
  // deliberate (user disconnect / unmount).
  const scheduleReconnect = useCallback(() => {
    if (intentionalCloseRef.current) return;
    clearReconnectTimer();
    const delay = backoffRef.current;
    backoffRef.current = Math.min(backoffRef.current * BACKOFF_MULTIPLIER, MAX_BACKOFF_MS);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      void connectInternal();
    }, delay);
  }, [clearReconnectTimer]);

  // The actual connection logic, re-invokable for reconnects. Tears down any
  // existing socket first (mirrors the reference hook's connectInternal).
  const connectInternal = useCallback(async () => {
    const sessionId = sessionIdRef.current;
    if (sessionId === null) return;
    // Re-entrancy guard: if a connect pass is already mid-flight, don't start a
    // second one. A convergence close fires scheduleReconnect AND the close
    // handler can race; without this guard both would open overlapping sockets
    // (the sink storm seen in the logs). The in-flight pass will finish and, if
    // it fails to co-locate, schedule exactly one more reconnect.
    if (connectingRef.current) return;
    connectingRef.current = true;

    // Tear down any existing socket so we never leave a dangling handler.
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    clearPing();

    setStatus('connecting');

    let wsUrl: string;
    try {
      wsUrl = await getPresignedWsUrl(sessionId);
    } catch {
      // Surface an error, but still retry unless the close was intentional.
      connectingRef.current = false;
      setStatus('error');
      scheduleReconnect();
      return;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      // This connect pass has settled (socket open): release the re-entrancy
      // guard so a later convergence close can start a fresh pass.
      connectingRef.current = false;
      // Announce the channel in the FIRST frame: AgentCore strips query params
      // and does not deliver our custom-channel header, so the agent routes
      // this connection as a display sink based on this hello. On reconnect the
      // agent hub replays its lastFrame in response, so the current card
      // reappears automatically (our equivalent of the reference's join_room
      // re-broadcast).
      ws.send(JSON.stringify({ type: 'hello', data: { channel: 'screenshare' } }));
      setStatus('live');
      // A successful open resets the backoff for the next disconnect.
      backoffRef.current = INITIAL_BACKOFF_MS;

      // A fresh socket starts NOT-yet-confirmed-co-located. AgentCore may have
      // routed us to a microVM that has no voice owner (the split), in which
      // case no display frame will ever arrive. Wait for the agent's
      // `colocation` signal: if voicePresent:true does not arrive within
      // COLOCATION_WAIT_MS we close and reconnect to reshuffle onto the voice
      // instance. Reconnecting from a CLOSED socket is what reshuffles the
      // instance assignment — holding the socket open (the old blind keepalive)
      // pinned us to the wrong microVM forever, which is exactly the bug.
      colocatedRef.current = false;
      clearColocationTimer();
      colocationTimerRef.current = setTimeout(() => {
        colocationTimerRef.current = null;
        if (colocatedRef.current) return; // confirmed co-located meanwhile
        if (intentionalCloseRef.current) return;
        // Not co-located in time: tear down this socket (its close handler
        // schedules a backoff reconnect that reshuffles the microVM).
        if (wsRef.current === ws) {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        }
      }, COLOCATION_WAIT_MS);
    };

    ws.onmessage = (evt: MessageEvent) => {
      const result = interpretFrame(evt.data);
      if (result === null) return;
      if (result.kind === 'display') {
        const display = result.display;
        setCurrent(display);
        setHistory((prev) => [...prev, display]);
      } else if (result.kind === 'transcript') {
        setTranscript((prev) => [...prev, result.line]);
      } else if (result.kind === 'meta') {
        // Meta frames only carry session metadata (never current/history/
        // transcript). Apply a title only when it's a non-empty string.
        const nextTitle = result.meta.title;
        if (typeof nextTitle === 'string' && nextTitle.length > 0) {
          setTitle(nextTitle);
        }
      } else if (result.kind === 'colocation') {
        if (result.voicePresent) {
          // Confirmed co-located with the voice stream on this microVM: stop the
          // reshuffle timer and hold the socket open with a light keepalive so
          // the idle-reaper doesn't drop it (which would re-trigger convergence).
          colocatedRef.current = true;
          clearColocationTimer();
          clearPing();
          pingRef.current = setInterval(() => {
            // Ping only THIS socket; after a reconnect a newer socket owns the
            // keepalive and this interval was already cleared.
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping', data: {} }));
            }
          }, PING_INTERVAL_MS);
        } else {
          // Explicitly told the voice owner is NOT here (e.g. it detached).
          // Drop the co-located flag and let the convergence timer (re)run; if
          // it already fired we simply wait for the next frame-driven cycle.
          colocatedRef.current = false;
        }
      }
    };

    ws.onerror = () => {
      connectingRef.current = false;
      clearPing();
      // A close event normally follows onerror and drives reconnect/status.
      // Don't flip to a hard error state if we're going to auto-reconnect —
      // keep the last card on screen during the transient blip.
      if (intentionalCloseRef.current) {
        setStatus('error');
      }
    };

    ws.onclose = () => {
      connectingRef.current = false;
      clearPing();
      clearColocationTimer();
      colocatedRef.current = false;
      wsRef.current = null;
      if (intentionalCloseRef.current) {
        setStatus('ended');
        return;
      }
      // Transient close: show 'connecting' (reconnecting) rather than 'ended'
      // so the UI keeps the last content instead of an ended/error state.
      // `current` is intentionally left intact — no flicker; the next frame
      // (or the lastFrame replay after re-hello) will update it.
      setStatus('connecting');
      scheduleReconnect();
    };
  }, [clearPing, clearColocationTimer, scheduleReconnect]);

  const connect = useCallback(
    (sessionId: string) => {
      // A fresh connect is an intentional (re)start: allow reconnects again and
      // reset backoff. Store the id so reconnects reuse it.
      intentionalCloseRef.current = false;
      backoffRef.current = INITIAL_BACKOFF_MS;
      sessionIdRef.current = sessionId;
      clearReconnectTimer();
      clearStaggerTimer();
      // One-time stagger: delay the FIRST connect so the voice connection can
      // establish and claim a microVM first. AgentCore then routes this later
      // same-session connection to that warm instance, so voice + screenshare
      // co-locate on the first try instead of racing onto different microVMs.
      // Show 'connecting' immediately so the UI doesn't look idle during the
      // delay. Reconnects go straight through connectInternal (no re-stagger).
      setStatus('connecting');
      staggerTimerRef.current = setTimeout(() => {
        staggerTimerRef.current = null;
        if (intentionalCloseRef.current) return;
        void connectInternal();
      }, INITIAL_STAGGER_MS);
    },
    [connectInternal, clearReconnectTimer, clearStaggerTimer],
  );

  const disconnect = useCallback(() => {
    // Deliberate close: suppress auto-reconnect and tear everything down.
    intentionalCloseRef.current = true;
    clearReconnectTimer();
    clearPing();
    clearColocationTimer();
    clearStaggerTimer();
    connectingRef.current = false;
    colocatedRef.current = false;
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('ended');
  }, [clearReconnectTimer, clearPing, clearColocationTimer, clearStaggerTimer]);

  // On unmount, deliberately close and clean up all timers/sockets so we never
  // reconnect after the component is gone.
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;
      clearReconnectTimer();
      clearPing();
      clearColocationTimer();
      clearStaggerTimer();
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [clearReconnectTimer, clearPing, clearColocationTimer, clearStaggerTimer]);

  return { status, current, history, transcript, title, connect, disconnect };
}
